import { FC, useState } from 'react';
import { Button, Space } from 'antd';
import { capitalCase } from 'change-case';
import { TbGripVertical, TbSquareRounded, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { DraggableProvidedDragHandleProps } from '@/shared/components/drag-and-drop';

interface MetricLineItemProps {
    displayLabel?: string;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    draggable?: boolean;
    fill?: string;
    isDragging?: boolean;
    label: string;
    onRemove?: () => void;
    stroke?: string;
}

const MetricLineItem: FC<MetricLineItemProps> = ({
    displayLabel,
    dragHandleProps,
    draggable = false,
    fill,
    isDragging = false,
    label,
    onRemove,
    stroke,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);

    return (
        <span
            style={{ display: 'inline-flex', marginRight: '0.5rem' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Space.Compact style={{ boxShadow: isDragging ? token.boxShadowSecondary : undefined, zIndex: 0 }}>
                <Button
                    size='small'
                    style={{
                        borderBottomRightRadius: onRemove ? undefined : token.borderRadius,
                        borderTopRightRadius: onRemove ? undefined : token.borderRadius,
                    }}
                >
                    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.1875rem' }}>
                        {draggable && (hovered || isDragging) ? (
                            <span
                                {...dragHandleProps}
                                aria-label={`Reorder ${capitalCase(label)}`}
                                role='button'
                                style={{
                                    alignItems: 'center',
                                    color: token.colorTextTertiary,
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <TbGripVertical />
                            </span>
                        ) : null}
                        <TbSquareRounded fill={fill} stroke={stroke} />
                        <span>{displayLabel ?? (label ? capitalCase(label) : '')}</span>
                    </span>
                </Button>
                {onRemove && (
                    <Button
                        aria-label={`Remove ${capitalCase(label)}`}
                        size='small'
                        style={{
                            borderBottomRightRadius: token.borderRadius,
                            borderTopRightRadius: token.borderRadius,
                        }}
                        onClick={onRemove}
                    >
                        <TbX style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />
                    </Button>
                )}
            </Space.Compact>
        </span>
    );
};

export default MetricLineItem;
